import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useToast } from '../../hooks/use-toast';
import api from '../../services/api';
import {
  Award,
  Building2,
  Users,
  ArrowLeft,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Eye,
  MoreHorizontal,
  UserPlus,
  GraduationCap,
  FileText,
  MessageSquare
} from 'lucide-react';

const AdminCertificaciones = () => {
  const [activeTab, setActiveTab] = useState('interesados');
  const [interesados, setInteresados] = useState([]);
  const [centros, setCentros] = useState([]);
  const [certificadores, setCertificadores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    // Allow access in development mode
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      // Try to fetch data - will work if user is logged in as admin
      const [intRes, centRes, certRes] = await Promise.all([
        api.get('/certifications/admin/interesados'),
        api.get('/certifications/admin/centros'),
        api.get('/certifications/admin/certificadores')
      ]);
      setInteresados(intRes.data);
      setCentros(centRes.data);
      setCertificadores(certRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      // For demo purposes, show empty state instead of error
      // In production, redirect to login
      setInteresados([]);
      setCentros([]);
      setCertificadores([]);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (type, id, newStatus) => {
    try {
      await api.put(`/certifications/admin/${type}/${id}/status?status=${newStatus}`);
      toast({ title: "Estado actualizado" });
      fetchAllData();
      setSelectedItem(null);
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo actualizar el estado",
        variant: "destructive"
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const statusConfig = {
    pending: { color: 'bg-yellow-100 text-yellow-700', label: 'Pendiente', icon: Clock },
    contacted: { color: 'bg-blue-100 text-blue-700', label: 'Contactado', icon: MessageSquare },
    enrolled: { color: 'bg-green-100 text-green-700', label: 'Inscrito', icon: CheckCircle },
    approved: { color: 'bg-green-100 text-green-700', label: 'Aprobado', icon: CheckCircle },
    partner: { color: 'bg-purple-100 text-purple-700', label: 'Partner', icon: Award },
    active: { color: 'bg-green-100 text-green-700', label: 'Activo', icon: CheckCircle },
    rejected: { color: 'bg-red-100 text-red-700', label: 'Rechazado', icon: XCircle },
    closed: { color: 'bg-gray-100 text-gray-700', label: 'Cerrado', icon: XCircle }
  };

  const tipoInteresLabels = {
    curso_upim: 'Cursos Academia UPIM',
    colegio: 'Colegio de Inmobiliarios',
    conocer_ec0110: 'CONOCER EC0110.02',
    conocer_otro: 'Otra certificación CONOCER'
  };

  const stats = {
    interesados: {
      total: interesados.length,
      pending: interesados.filter(i => i.status === 'pending').length
    },
    centros: {
      total: centros.length,
      pending: centros.filter(c => c.status === 'pending').length
    },
    certificadores: {
      total: certificadores.length,
      pending: certificadores.filter(c => c.status === 'pending').length
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Gestión de Certificaciones</h1>
                <p className="text-sm text-gray-500">Solicitudes de formación académica</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-medium">Interesados</p>
                  <p className="text-2xl font-bold text-blue-900">{stats.interesados.total}</p>
                </div>
                <div className="h-10 w-10 rounded-full bg-blue-200 flex items-center justify-center">
                  <UserPlus className="h-5 w-5 text-blue-700" />
                </div>
              </div>
              {stats.interesados.pending > 0 && (
                <Badge className="mt-2 bg-blue-200 text-blue-800">
                  {stats.interesados.pending} pendientes
                </Badge>
              )}
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600 font-medium">Centros</p>
                  <p className="text-2xl font-bold text-purple-900">{stats.centros.total}</p>
                </div>
                <div className="h-10 w-10 rounded-full bg-purple-200 flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-purple-700" />
                </div>
              </div>
              {stats.centros.pending > 0 && (
                <Badge className="mt-2 bg-purple-200 text-purple-800">
                  {stats.centros.pending} pendientes
                </Badge>
              )}
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-medium">Certificadores</p>
                  <p className="text-2xl font-bold text-green-900">{stats.certificadores.total}</p>
                </div>
                <div className="h-10 w-10 rounded-full bg-green-200 flex items-center justify-center">
                  <Award className="h-5 w-5 text-green-700" />
                </div>
              </div>
              {stats.certificadores.pending > 0 && (
                <Badge className="mt-2 bg-green-200 text-green-800">
                  {stats.certificadores.pending} pendientes
                </Badge>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="interesados" className="flex items-center gap-2">
              <UserPlus className="h-4 w-4" />
              Interesados
              {stats.interesados.pending > 0 && (
                <Badge className="ml-1 bg-yellow-100 text-yellow-700 text-xs">
                  {stats.interesados.pending}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="centros" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Centros
              {stats.centros.pending > 0 && (
                <Badge className="ml-1 bg-yellow-100 text-yellow-700 text-xs">
                  {stats.centros.pending}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="certificadores" className="flex items-center gap-2">
              <Award className="h-4 w-4" />
              Certificadores
              {stats.certificadores.pending > 0 && (
                <Badge className="ml-1 bg-yellow-100 text-yellow-700 text-xs">
                  {stats.certificadores.pending}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : (
            <>
              {/* Interesados Tab */}
              <TabsContent value="interesados">
                {interesados.length === 0 ? (
                  <Card className="text-center py-12">
                    <CardContent>
                      <UserPlus className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">No hay solicitudes de interesados</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {interesados.map(item => {
                      const status = statusConfig[item.status] || statusConfig.pending;
                      return (
                        <Card key={item.id} className="hover:shadow-md transition-shadow">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex items-start space-x-4 flex-1">
                                <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                                  <GraduationCap className="h-5 w-5 text-blue-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center space-x-2 mb-1">
                                    <h3 className="font-semibold text-gray-900">{item.nombre}</h3>
                                    <Badge className={status.color}>
                                      <status.icon className="h-3 w-3 mr-1" />
                                      {status.label}
                                    </Badge>
                                  </div>
                                  <div className="flex flex-wrap gap-3 text-sm text-gray-500 mb-2">
                                    <span className="flex items-center">
                                      <Mail className="h-3.5 w-3.5 mr-1" />
                                      {item.email}
                                    </span>
                                    <span className="flex items-center">
                                      <Phone className="h-3.5 w-3.5 mr-1" />
                                      {item.telefono}
                                    </span>
                                    <span className="flex items-center">
                                      <MapPin className="h-3.5 w-3.5 mr-1" />
                                      {item.ciudad}
                                    </span>
                                  </div>
                                  <Badge variant="outline" className="mr-2">
                                    {tipoInteresLabels[item.tipo_interes] || item.tipo_interes}
                                  </Badge>
                                  {item.mensaje && (
                                    <p className="text-sm text-gray-600 mt-2 bg-gray-50 p-2 rounded">
                                      "{item.mensaje}"
                                    </p>
                                  )}
                                  <p className="text-xs text-gray-400 mt-2">
                                    <Calendar className="h-3 w-3 inline mr-1" />
                                    {formatDate(item.created_at)}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2 ml-4">
                                {item.status === 'pending' && (
                                  <>
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      className="text-blue-600"
                                      onClick={() => updateStatus('interesados', item.id, 'contacted')}
                                    >
                                      Contactado
                                    </Button>
                                    <Button 
                                      size="sm"
                                      className="bg-green-600 hover:bg-green-700"
                                      onClick={() => updateStatus('interesados', item.id, 'enrolled')}
                                    >
                                      Inscrito
                                    </Button>
                                  </>
                                )}
                                {item.status === 'contacted' && (
                                  <Button 
                                    size="sm"
                                    className="bg-green-600 hover:bg-green-700"
                                    onClick={() => updateStatus('interesados', item.id, 'enrolled')}
                                  >
                                    Marcar Inscrito
                                  </Button>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </TabsContent>

              {/* Centros Tab */}
              <TabsContent value="centros">
                {centros.length === 0 ? (
                  <Card className="text-center py-12">
                    <CardContent>
                      <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">No hay solicitudes de centros</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {centros.map(item => {
                      const status = statusConfig[item.status] || statusConfig.pending;
                      return (
                        <Card key={item.id} className="hover:shadow-md transition-shadow">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex items-start space-x-4 flex-1">
                                <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                                  <Building2 className="h-5 w-5 text-purple-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center space-x-2 mb-1">
                                    <h3 className="font-semibold text-gray-900">{item.nombre_centro}</h3>
                                    <Badge className={status.color}>
                                      <status.icon className="h-3 w-3 mr-1" />
                                      {status.label}
                                    </Badge>
                                  </div>
                                  <p className="text-sm text-gray-600 mb-2">Representante: {item.representante}</p>
                                  <div className="flex flex-wrap gap-3 text-sm text-gray-500 mb-2">
                                    <span className="flex items-center">
                                      <Mail className="h-3.5 w-3.5 mr-1" />
                                      {item.email}
                                    </span>
                                    <span className="flex items-center">
                                      <Phone className="h-3.5 w-3.5 mr-1" />
                                      {item.telefono}
                                    </span>
                                    <span className="flex items-center">
                                      <MapPin className="h-3.5 w-3.5 mr-1" />
                                      {item.ciudad}, {item.estado}
                                    </span>
                                  </div>
                                  <div className="bg-gray-50 p-3 rounded-lg text-sm mt-2">
                                    <p className="font-medium text-gray-700 mb-1">Descripción:</p>
                                    <p className="text-gray-600">{item.descripcion}</p>
                                    <p className="font-medium text-gray-700 mt-2 mb-1">Experiencia:</p>
                                    <p className="text-gray-600">{item.experiencia_certificacion}</p>
                                  </div>
                                  <p className="text-xs text-gray-400 mt-2">
                                    <Calendar className="h-3 w-3 inline mr-1" />
                                    {formatDate(item.created_at)}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2 ml-4">
                                {item.status === 'pending' && (
                                  <>
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      className="text-red-600"
                                      onClick={() => updateStatus('centros', item.id, 'rejected')}
                                    >
                                      Rechazar
                                    </Button>
                                    <Button 
                                      size="sm"
                                      className="bg-purple-600 hover:bg-purple-700"
                                      onClick={() => updateStatus('centros', item.id, 'approved')}
                                    >
                                      Aprobar
                                    </Button>
                                  </>
                                )}
                                {item.status === 'approved' && (
                                  <Button 
                                    size="sm"
                                    className="bg-green-600 hover:bg-green-700"
                                    onClick={() => updateStatus('centros', item.id, 'partner')}
                                  >
                                    Hacer Partner
                                  </Button>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </TabsContent>

              {/* Certificadores Tab */}
              <TabsContent value="certificadores">
                {certificadores.length === 0 ? (
                  <Card className="text-center py-12">
                    <CardContent>
                      <Award className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">No hay solicitudes de certificadores</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {certificadores.map(item => {
                      const status = statusConfig[item.status] || statusConfig.pending;
                      return (
                        <Card key={item.id} className="hover:shadow-md transition-shadow">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex items-start space-x-4 flex-1">
                                <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                                  <Award className="h-5 w-5 text-green-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center space-x-2 mb-1">
                                    <h3 className="font-semibold text-gray-900">{item.nombre}</h3>
                                    <Badge className={status.color}>
                                      <status.icon className="h-3 w-3 mr-1" />
                                      {status.label}
                                    </Badge>
                                    {item.ya_registrado_conocer && (
                                      <Badge className="bg-blue-100 text-blue-700">
                                        Ya registrado CONOCER
                                      </Badge>
                                    )}
                                  </div>
                                  <div className="flex flex-wrap gap-3 text-sm text-gray-500 mb-2">
                                    <span className="flex items-center">
                                      <Mail className="h-3.5 w-3.5 mr-1" />
                                      {item.email}
                                    </span>
                                    <span className="flex items-center">
                                      <Phone className="h-3.5 w-3.5 mr-1" />
                                      {item.telefono}
                                    </span>
                                    <span className="flex items-center">
                                      <MapPin className="h-3.5 w-3.5 mr-1" />
                                      {item.ciudad}
                                    </span>
                                  </div>
                                  <div className="flex flex-wrap gap-2 mb-2">
                                    <Badge variant="outline">
                                      Especialidad: {item.especialidad}
                                    </Badge>
                                    <Badge variant="outline">
                                      {item.experiencia_anios} años exp.
                                    </Badge>
                                    {item.numero_conocer && (
                                      <Badge variant="outline" className="bg-blue-50">
                                        #{item.numero_conocer}
                                      </Badge>
                                    )}
                                  </div>
                                  {item.mensaje && (
                                    <p className="text-sm text-gray-600 mt-2 bg-gray-50 p-2 rounded">
                                      "{item.mensaje}"
                                    </p>
                                  )}
                                  <p className="text-xs text-gray-400 mt-2">
                                    <Calendar className="h-3 w-3 inline mr-1" />
                                    {formatDate(item.created_at)}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2 ml-4">
                                {item.status === 'pending' && (
                                  <>
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      className="text-red-600"
                                      onClick={() => updateStatus('certificadores', item.id, 'rejected')}
                                    >
                                      Rechazar
                                    </Button>
                                    <Button 
                                      size="sm"
                                      className="bg-green-600 hover:bg-green-700"
                                      onClick={() => updateStatus('certificadores', item.id, 'approved')}
                                    >
                                      Aprobar
                                    </Button>
                                  </>
                                )}
                                {item.status === 'approved' && (
                                  <Button 
                                    size="sm"
                                    className="bg-blue-600 hover:bg-blue-700"
                                    onClick={() => updateStatus('certificadores', item.id, 'active')}
                                  >
                                    Activar
                                  </Button>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </TabsContent>
            </>
          )}
        </Tabs>
      </div>
    </div>
  );
};

export default AdminCertificaciones;
